/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

// Component imports
import { Button, Card, Col, Divider, Form, Row } from 'antd';

// Misc imports
import useTheme from '../../utils/hooks/useTheme';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';

// Constant declarations
const baseClassName = 'form-card';

/**
 * Form Card component, renders a Form inside a Card with a fixed height and a sticky footer
 * @param {node} children The contents of the form, usually Form.Item components
  * @param {number} cardHeight The height of the card
  * @param {node} footer The footer of the card
  * @param {object} form The form object to use
  * @param {object} formProps Any other props that would be valid for an Ant Design Form component
 */
const FormCard = ({
  children,
  cardHeight,
  footerActions,
  form,
  ...formProps
}) => {
  const { botFormPanelHeight } = useTheme();
  const cardRef = React.createRef();
  const footerRef = React.createRef();
  const [footerHeight, setFooterHeight] = useState(0);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [shouldShowArrow, setShouldShowArrow] = useState(false);

  useEffect(() => {
    if (footerRef.current) {
      setFooterHeight(footerRef.current.clientHeight);
    } else {
      setFooterHeight(0);
    }
  }, [footerRef.current, form.isFieldsTouched(), footerActions]);

  // Check scroll position and content height
  const checkScrollPosition = useCallback(() => {
    const cardBody = cardRef.current?.querySelector('.ant-card-body');
    if (cardBody) {
      // Check if content exceeds threshold (960px or viewport height)
      const contentExceedsThreshold = cardBody.scrollHeight > Math.min(960, cardBody.clientHeight);
      setShouldShowArrow(contentExceedsThreshold);

      // Check if we're at the bottom (with a small threshold for precision)
      const isBottom = Math.abs((cardBody.scrollHeight - cardBody.scrollTop - cardBody.clientHeight)) < 5;
      setIsAtBottom(isBottom);
    }
  }, [cardRef]);

  // Set up scroll event listener
  useEffect(() => {
    const cardBody = cardRef.current?.querySelector('.ant-card-body');
    if (cardBody) {
      cardBody.addEventListener('scroll', checkScrollPosition);
      // Initial check
      checkScrollPosition();
    }

    return () => {
      if (cardBody) {
        cardBody.removeEventListener('scroll', checkScrollPosition);
      }
    };
  }, [cardRef.current, checkScrollPosition]);

  const scrollToBottom = () => {
    const cardBody = cardRef.current.querySelector('.ant-card-body');
    cardBody.scrollTo({
      top: cardBody.scrollHeight,
      behavior: 'smooth'
    });
  };

  const scrollToTop = () => {
    const cardBody = cardRef.current.querySelector('.ant-card-body');
    cardBody.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  /**
   * Renders a floating button that scrolls to the bottom/top of the card
   */
  const renderScrollButton = () => {
    if (!shouldShowArrow) {
      return null;
    }

    return (
      <Button
        className="position-absolute right-0 z-index-affix mx-8 my-4"
        onClick={isAtBottom ? scrollToTop : scrollToBottom}
        shape="circle"
        size="large"
        type="default"
        style={{ bottom: footerActions && footerActions.length > 0 && form.isFieldsTouched() ? footerHeight : 0 }}
      >
        <div
          className={`${baseClassName}__arrow-icon`}
          style={{
            transform: isAtBottom ? 'rotate(180deg)' : 'rotate(0deg)'
          }}
        >
          <ArrowDownIcon className="fs-lg" />
        </div>
      </Button>
    );
  };

  return (
    <Form
      form={form}
      {...formProps}
    >
      <Card
        className="p-0"
        ref={cardRef}
        bodyStyle={{
          minHeight: 480,
          maxHeight: cardHeight || botFormPanelHeight,
          overflowX: 'hidden',
          overflowY: 'auto'
        }}
      >
        {renderScrollButton()}

        <Row gutter={24}>
          <Col span={24} className="py-4 px-8">
            {children}
          </Col>
        </Row>

        {
          footerActions && footerActions.length > 0 && form.isFieldsTouched() ? (
            <Row
              className="position-sticky bottom-0 bg-white"
              ref={footerRef}
            >
              <Divider className="my-0" />

              <Col flex="auto" span={24} className="m-4">
                <Row gutter={8} justify="end">
                  {
                    footerActions.map((action, index) => (
                      <Col key={index}>
                        {action}
                      </Col>
                    ))
                  }
                </Row>
              </Col>
            </Row>
          ) : null
        }
      </Card>
    </Form>
  );
};

FormCard.propTypes = {
  children: PropTypes.node,
  cardHeight: PropTypes.number,
  form: PropTypes.object,
  footerActions: PropTypes.array
};

export default FormCard;
